/**
 * A source's example message, flattened into the paths a pre-mapping can name.
 *
 * Pure, and in `src/lib` rather than inside a component, because vitest here only
 * runs `src/**\/*.test.ts`; both the Pre-Mapping and Mapping screens read from it.
 *
 * ## The flattening
 *
 * Each value's path is the keys needed to reach it, concatenated with `.`:
 * `observations[*].code.code`. An array contributes `[*]` — "whichever entry" —
 * never a fixed index, because a laboratory reorders a panel freely and a name
 * pinned to entry 0 would read a different analyte next week. That is also the
 * spelling the backend resolves, so a path shown here is exactly what is stored.
 *
 * An array's entries are MERGED: a field only the third observation carries is
 * still offered. Only values are listed — an object or a list cannot go into a
 * form field, so they are routes to a value, not values.
 */

export type ExampleValueKind = 'string' | 'number' | 'boolean' | 'null'

export interface FlatPath {
  /** The concatenated keys: `observations[*].valueNumeric`. */
  path: string
  /** One value from the example, so the field is recognisable. */
  sample: string
  kind: ExampleValueKind
  /** True for a field sitting directly on the message, not inside anything. */
  topLevel: boolean
}

/** A ceiling, so a pathological example degrades rather than wedges the screen. */
export const MAX_PATHS = 2000
const MAX_DEPTH = 12

function kindOf(value: unknown): ExampleValueKind | null {
  if (value === null) return 'null'
  if (typeof value === 'string') return 'string'
  if (typeof value === 'number') return 'number'
  if (typeof value === 'boolean') return 'boolean'
  return null
}

function render(value: unknown): string {
  if (value === null) return 'null'
  const text = String(value)
  return text.length > 60 ? `${text.slice(0, 57)}…` : text
}

/** Every entry's keys merged, first non-null value per key kept as the sample. */
function mergeEntries(items: readonly unknown[]): unknown {
  const objects = items.filter(
    (item): item is Record<string, unknown> =>
      item !== null && typeof item === 'object' && !Array.isArray(item),
  )
  if (objects.length === 0) return items.find((item) => item !== undefined)
  const merged: Record<string, unknown> = {}
  for (const object of objects) {
    for (const [key, value] of Object.entries(object)) {
      if (!(key in merged) || merged[key] === null) merged[key] = value
      else if (Array.isArray(merged[key]) && Array.isArray(value)) {
        merged[key] = [...(merged[key] as unknown[]), ...value]
      }
    }
  }
  return merged
}

export function flattenExample(example: unknown): FlatPath[] {
  const out: FlatPath[] = []

  const walk = (value: unknown, path: string, depth: number) => {
    if (out.length >= MAX_PATHS || depth > MAX_DEPTH) return
    const kind = kindOf(value)
    if (kind !== null) {
      if (path !== '') {
        out.push({ path, sample: render(value), kind, topLevel: !/[.[]/.test(path) })
      }
      return
    }
    if (Array.isArray(value)) {
      if (value.length === 0) return
      walk(mergeEntries(value), `${path}[*]`, depth + 1)
      return
    }
    if (value && typeof value === 'object') {
      for (const [key, child] of Object.entries(value as Record<string, unknown>)) {
        walk(child, path === '' ? key : `${path}.${key}`, depth + 1)
      }
    }
  }

  walk(example, '', 0)
  return out
}

/** The fields sitting directly on the message — offered without a name. */
export function topLevelPaths(example: unknown): FlatPath[] {
  return flattenExample(example).filter((p) => p.topLevel)
}

/** Parse an uploaded or pasted example, with the reason when it is not usable. */
export function parseExample(text: string): { ok: true; value: unknown } | { ok: false; error: string } {
  const trimmed = text.trim()
  if (trimmed === '') return { ok: false, error: 'Paste or upload one message from this source.' }
  let value: unknown
  try {
    value = JSON.parse(trimmed)
  } catch (err) {
    return { ok: false, error: `Not valid JSON: ${(err as Error).message}` }
  }
  if (value === null || typeof value !== 'object') {
    return { ok: false, error: 'The example has to be a JSON object — the message as it arrives.' }
  }
  if (flattenExample(value).length === 0) {
    return { ok: false, error: 'The example has no values in it, so there are no paths to name.' }
  }
  return { ok: true, value }
}
